import { FormEvent, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  BankkontoInput,
  ZahlungskonditionInput,
  createBankkonto,
  createZahlungskondition,
  deleteBankkonto,
  deleteZahlungskondition,
  fetchBankkonten,
  fetchZahlungskonditionen,
} from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { Badge, Button, FieldError, Input } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { RequireRole } from "@/auth/AuthProvider"
import { formatZahl } from "@/lib/format"

const LEER_ZK: Omit<ZahlungskonditionInput, "organisationId"> = {
  bezeichnung: "",
  zahlungszielTage: null,
  skontoProzent: null,
}

const LEER_BK: Omit<BankkontoInput, "organisationId"> = {
  iban: "",
  bic: "",
  bank: "",
  waehrung: "EUR",
  hauptbank: false,
}

/**
 * Finanzen-Sektion (Zahlungskonditionen + Bankkonten) innerhalb der
 * Organisation-Detailseite — AP-2.9. Nur sichtbar mit der Rolle
 * `app:stammdaten:finanzen` (bzw. `admin`); Backend setzt dasselbe Gate
 * unabhängig durch (`require_permission_set("finanzen")`).
 */
export function FinanzenSection({ organisationId }: { organisationId: string }) {
  return (
    <RequireRole role="app:stammdaten:finanzen">
      <div className="space-y-6">
        <ZahlungskonditionenBlock organisationId={organisationId} />
        <BankkontenBlock organisationId={organisationId} />
      </div>
    </RequireRole>
  )
}

function ZahlungskonditionenBlock({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [form, setForm] = useState(LEER_ZK)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["zahlungskonditionen", organisationId],
    queryFn: () => fetchZahlungskonditionen(organisationId),
  })

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["zahlungskonditionen", organisationId] })

  const anlegen = useMutation({
    mutationFn: () => createZahlungskondition({ ...form, organisationId }),
    onSuccess: () => {
      invalidieren()
      setForm(LEER_ZK)
      setZeigeNeu(false)
      toast.show("Zahlungskondition gespeichert.", "success")
    },
    onError: () => toast.show("Zahlungskondition konnte nicht gespeichert werden.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteZahlungskondition(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("Zahlungskondition gelöscht.", "success")
    },
    onError: () => toast.show("Zahlungskondition konnte nicht gelöscht werden.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    anlegen.mutate()
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Zahlungskonditionen</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Kondition hinzufügen
        </button>
      </div>

      {isLoading && <SkeletonZeile className="h-10 w-full" />}

      <ul className="space-y-2">
        {data?.map((zk) => (
          <li
            key={zk.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">{zk.bezeichnung ?? "Ohne Bezeichnung"}</p>
              <p className="text-slate-500">
                Zahlungsziel: {zk.zahlungszielTage != null ? `${zk.zahlungszielTage} Tage` : "—"}
                {" · "}
                Skonto: {zk.skontoProzent != null ? `${formatZahl(zk.skontoProzent)} %` : "—"}
              </p>
            </div>
            {loeschenId === zk.id ? (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600">Wirklich löschen?</span>
                <button
                  type="button"
                  onClick={() => loeschen.mutate(zk.id)}
                  disabled={loeschen.isPending}
                  className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Ja, löschen
                </button>
                <button
                  type="button"
                  onClick={() => setLoeschenId(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Abbrechen
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setLoeschenId(zk.id)}
                className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Zahlungskondition erfasst."
          aktion={{ label: "+ Kondition hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="zahlungskondition-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Zahlungskondition</SheetTitle>
            <SheetDescription>Zahlungskondition für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="zk-neu-form" onSubmit={onSubmit} className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Bezeichnung</label>
              <Input
                value={form.bezeichnung ?? ""}
                onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  Zahlungsziel (Tage)
                </label>
                <Input
                  type="number"
                  value={form.zahlungszielTage ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      zahlungszielTage: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Skonto %</label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.skontoProzent ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      skontoProzent: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                />
              </div>
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="zk-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Kondition speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}

function BankkontenBlock({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [form, setForm] = useState(LEER_BK)
  const [ibanFehler, setIbanFehler] = useState<string | null>(null)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["bankkonten", organisationId],
    queryFn: () => fetchBankkonten(organisationId),
  })

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["bankkonten", organisationId] })

  const anlegen = useMutation({
    mutationFn: () => createBankkonto({ ...form, organisationId }),
    onSuccess: () => {
      invalidieren()
      setForm(LEER_BK)
      setZeigeNeu(false)
      toast.show("Bankkonto gespeichert.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Bankkonto konnte nicht gespeichert werden.", "error")
    },
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteBankkonto(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("Bankkonto gelöscht.", "success")
    },
    onError: () => toast.show("Bankkonto konnte nicht gelöscht werden.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.iban.trim()) {
      setIbanFehler("IBAN ist erforderlich.")
      document.getElementById("bankkonto-iban")?.focus()
      return
    }
    setIbanFehler(null)
    anlegen.mutate()
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Bankverbindungen</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Bankkonto hinzufügen
        </button>
      </div>

      {isLoading && <SkeletonZeile className="h-10 w-full" />}

      <ul className="space-y-2">
        {data?.map((bk) => (
          <li
            key={bk.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-mono font-medium text-slate-900">
                {bk.iban}
                {!bk.ibanOk && (
                  <Badge tone="red" className="ml-2 font-sans">
                    IBAN ungültig
                  </Badge>
                )}
                {bk.hauptbank && (
                  <Badge tone="slate" className="ml-2 font-sans">
                    Hauptbank
                  </Badge>
                )}
              </p>
              <p className="text-slate-500">
                {[bk.bank, bk.bic, bk.waehrung].filter(Boolean).join(" · ") || "—"}
              </p>
            </div>
            {loeschenId === bk.id ? (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600">Wirklich löschen?</span>
                <button
                  type="button"
                  onClick={() => loeschen.mutate(bk.id)}
                  disabled={loeschen.isPending}
                  className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Ja, löschen
                </button>
                <button
                  type="button"
                  onClick={() => setLoeschenId(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Abbrechen
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setLoeschenId(bk.id)}
                className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Bankverbindung erfasst."
          aktion={{ label: "+ Bankkonto hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="bankkonto-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neues Bankkonto</SheetTitle>
            <SheetDescription>Bankverbindung für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="bankkonto-neu-form" onSubmit={onSubmit} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="bankkonto-iban" className="block text-xs font-medium text-slate-700">
                IBAN *
              </label>
              <Input
                id="bankkonto-iban"
                required
                placeholder="DE12 3456 7890 1234 5678 90"
                value={form.iban}
                onChange={(e) => {
                  setForm({ ...form, iban: e.target.value })
                  if (ibanFehler) setIbanFehler(null)
                }}
                invalid={!!ibanFehler}
                aria-describedby={ibanFehler ? "bankkonto-iban-fehler" : undefined}
                className="font-mono"
              />
              <FieldError id="bankkonto-iban-fehler">{ibanFehler}</FieldError>
              <p className="mt-1 text-xs text-slate-500">
                Die Prüfsumme wird beim Speichern serverseitig validiert; ungültige IBANs werden
                trotzdem gespeichert, aber als „IBAN ungültig" markiert.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">BIC</label>
                <Input
                  value={form.bic ?? ""}
                  onChange={(e) => setForm({ ...form, bic: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Bank</label>
                <Input
                  value={form.bank ?? ""}
                  onChange={(e) => setForm({ ...form, bank: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Währung</label>
                <Input
                  value={form.waehrung ?? ""}
                  onChange={(e) => setForm({ ...form, waehrung: e.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.hauptbank ?? false}
                onChange={(e) => setForm({ ...form, hauptbank: e.target.checked })}
              />
              Als Hauptbank setzen
            </label>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="bankkonto-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Bankkonto speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
